import { type Route } from './+types/index.ts'

export const meta: Route.MetaFunction = () => [
	{ title: 'frisbeegolfradat.fi — uudistuu' },
]

export default function Index() {
	return (
		<>
			<Hero />
			<MissionSection />
			<TechnicalDebtSection />
			<MaintenanceSection />
			<SharedProcessesSection />
			<DataFragmentationSection />
			<FeedbackCta />
			<RoadmapSection />
		</>
	)
}

/* ────────────────────────────────────────────────────────────────────────── */
/* Hero                                                                       */
/* ────────────────────────────────────────────────────────────────────────── */

function Hero() {
	return (
		<section className="from-primary/10 relative flex min-h-[85vh] flex-col justify-center overflow-hidden bg-linear-to-b to-transparent">
			<div className="container py-24 text-center md:py-32">
				<p className="text-primary text-sm font-semibold tracking-widest uppercase">
					frisbeegolfradat.fi · 2.0
				</p>
				<h1 className="mt-6 text-4xl font-bold tracking-tight md:text-6xl">
					Valmis muutokseen?
				</h1>
				<p className="text-muted-foreground mx-auto mt-6 max-w-2xl text-lg leading-relaxed md:text-xl">
					Uudistus ei tule olemaan vain teknologian päivitys — haluamme samalla
					rakentaa parempaa frisbeegolfyhteisöä.
				</p>
				<div className="mt-16 flex justify-center" aria-hidden="true">
					<svg
						xmlns="http://www.w3.org/2000/svg"
						viewBox="0 0 24 24"
						fill="none"
						stroke="currentColor"
						strokeWidth="2"
						strokeLinecap="round"
						strokeLinejoin="round"
						className="text-primary/60 size-6 animate-bounce"
					>
						<polyline points="6 9 12 15 18 9" />
					</svg>
				</div>
			</div>
		</section>
	)
}

/* ────────────────────────────────────────────────────────────────────────── */
/* Mission — what frisbeegolfradat is today                                   */
/* ────────────────────────────────────────────────────────────────────────── */

const STATS: { eyebrow?: string; value: string; label: string }[] = [
	{ value: '1,7M', label: 'sivulatausta vuonna 2025' },
	{ value: '600K+', label: 'latausta jo tänä vuonna' },
	{ eyebrow: 'Suomen', value: '#1', label: 'hakukoneessa' },
]

function MissionSection() {
	return (
		<section
			id="mission"
			className="bg-background flex min-h-[85vh] flex-col justify-center"
		>
			<div className="container py-28 md:py-40">
				<div className="mx-auto max-w-2xl text-center">
					<div className="text-primary text-xs font-semibold tracking-widest uppercase">
						Tausta
					</div>
					<SectionTitle>
						Mikä Frisbeegolfradat.fi on tänään?
					</SectionTitle>
					<p className="text-muted-foreground mt-4 text-lg leading-relaxed">
						Suomen luotettavin lähde frisbeegolfratojen tiedoille — paikka,
						josta uudet harrastajat löytävät ensimmäiset ratansa ja kokeneetkin
						pelaajat etsivät uutta pelattavaa.
					</p>
				</div>

				<div className="mt-16 grid gap-px bg-border md:grid-cols-3 md:gap-px ring-1 ring-border overflow-hidden">
					{STATS.map((s) => (
						<div
							key={s.label}
							className="bg-background flex flex-col items-center justify-center p-10 text-center"
						>
							<div className="text-primary flex flex-col items-center leading-none">
								{s.eyebrow ? (
									<span className="text-base font-semibold tracking-widest uppercase opacity-80">
										{s.eyebrow}
									</span>
								) : null}
								<span className="mt-1 text-5xl font-bold tracking-tight md:text-6xl">
									{s.value}
								</span>
							</div>
							<div className="text-muted-foreground mt-3 max-w-56 text-sm leading-snug">
								{s.label}
							</div>
						</div>
					))}
				</div>

				<p className="mx-auto mt-16 max-w-2xl text-center text-base leading-relaxed text-foreground/80 md:text-lg">
					Haluamme jatkossakin, että Frisbeegolfradat on luotettava paikka
					tiedolle — innostaa uusia pelaajia radoille ja auttaa kokeneitakin
					löytämään mielenkiintoista uutta pelattavaa.
				</p>
			</div>
		</section>
	)
}

/* ────────────────────────────────────────────────────────────────────────── */
/* 01 — Tekninen velka: side-by-side cards                                    */
/* ────────────────────────────────────────────────────────────────────────── */

function TechnicalDebtSection() {
	const cascade = [
		{
			heading: 'Vanhentunut teknologia',
			body: 'Sivusto on rakennettu vanhentuneella teknologialla. Koodi ja sisältö ovat täynnä päivittämättömiä asioita, ja kehittämisestä on tullut hidasta ja työlästä.',
		},
		{
			heading: 'Pysähtynyt kehitys',
			body: 'Kehitys on käytännössä pysähtynyt: uudet ominaisuudet jäävät tekemättä ja käyttäjien toiveet kuulematta.',
		},
		{
			heading: 'Heikkenevä luottamus',
			body: 'Kun ongelmat alkavat näkyä loppukäyttäjille, luottamus sivustoon ja sen tietoihin heikkenee — ja vaihtoehtoja etsitään muualta.',
		},
	]
	const solutions = [
		'Säilytetään se, mikä toimii.',
		'Siivotaan tarpeettomat pois.',
		'Ketterä ja nopea jatkokehitys resurssien puitteissa.',
		'Mobile-first — tietokonekäyttäjiä unohtamatta.',
	]

	return (
		<section
			id="tekninen-velka"
			className="bg-background flex min-h-[85vh] flex-col justify-center"
		>
			<div className="container py-28 md:py-40">
				{/* Title block */}
				<div>
					<SectionNumber>01</SectionNumber>
					<SectionTitle>Tekninen velka</SectionTitle>
					<p className="text-muted-foreground mt-4 max-w-2xl text-base leading-relaxed md:text-lg">
						Nykyinen alusta on jäänyt aikaansa kiinni — ja hidastaa kaikkea
						kehitystä.
					</p>
				</div>

				{/* Nykytila — cascade of three linked boxes */}
				<div className="mt-14">
					<ToneLabel tone="problem">Nykytila</ToneLabel>
					<div className="mt-6 grid gap-4 md:grid-cols-3">
						{cascade.map((step, i) => (
							<div key={step.heading} className="relative">
								<div className="border-destructive/30 bg-destructive/5 border-l-4 h-full p-5">
									<div className="text-destructive font-mono text-xs font-semibold tracking-widest">
										{String(i + 1).padStart(2, '0')}
									</div>
									<h3 className="text-foreground mt-2 text-base font-bold tracking-tight">
										{step.heading}
									</h3>
									<p className="text-foreground/90 mt-2 text-sm leading-relaxed">
										{step.body}
									</p>
								</div>
								{i < cascade.length - 1 ? (
									<svg
										xmlns="http://www.w3.org/2000/svg"
										viewBox="0 0 24 24"
										fill="none"
										stroke="currentColor"
										strokeWidth="2"
										strokeLinecap="round"
										strokeLinejoin="round"
										aria-hidden="true"
										className="text-destructive/50 absolute top-1/2 -right-3.5 hidden size-5 -translate-y-1/2 md:block"
									>
										<polyline points="9 18 15 12 9 6" />
									</svg>
								) : null}
							</div>
						))}
					</div>
				</div>

				{/* Ratkaisu — full-width panel below */}
				<div className="border-primary/30 bg-primary/5 mt-10 border-l-4 p-6 md:p-8">
					<ToneLabel tone="solution">Ratkaisu</ToneLabel>
					<p className="text-foreground mt-3 text-lg leading-relaxed font-semibold md:text-xl">
						Sovellus rakennetaan kokonaan uudelleen modernilla pohjalla.
					</p>
					<ul className="mt-6 grid gap-3 md:grid-cols-2 md:gap-x-10 md:gap-y-4">
						{solutions.map((s) => (
							<li
								key={s}
								className="text-foreground/90 flex gap-3 text-base leading-relaxed"
							>
								<span className="text-primary mt-2 inline-block size-1.5 shrink-0 rounded-full bg-current" />
								<span>{s}</span>
							</li>
						))}
					</ul>
				</div>
			</div>
		</section>
	)
}

/* ────────────────────────────────────────────────────────────────────────── */
/* 02 — Ratatietojen ylläpito: big quote → numbered solution steps           */
/* ────────────────────────────────────────────────────────────────────────── */

function MaintenanceSection() {
	const steps = [
		{
			title: 'Päivitys suoraan ratasivulta',
			body: 'Kaikki kirjautuneet käyttäjät voivat päivittää ratatietoja helposti suoraan ratasivulta.',
		},
		{
			title: 'Vertaisarviointi',
			body: 'Aktiiviset käyttäjät hyväksyvät muutokset. Luotettavien tekijöiden muutokset menevät suoraan läpi.',
		},
		{
			title: 'Muutoshistoria',
			body: 'Kaikki muutokset tallennetaan, ja muutoshistoria on näkyvillä.',
		},
		{
			title: 'Ylläpidon rooli',
			body: 'Ylläpitäjät keskittyvät moderointiin, eivät tiedonsyöttöön.',
		},
		{
			title: 'Aktiivisten palkitseminen',
			body: 'Mietitään palkitsemismallia aktiivisille tekijöille — kokemus, status ja/tai konkreettisia etuja.',
		},
	]

	return (
		<section
			id="ratatietojen-yllapito"
			className="bg-muted/40 border-border/60 flex min-h-[85vh] flex-col justify-center border-y"
		>
			<div className="container py-28 md:py-40">
				<div className="max-w-4xl">
					<SectionNumber>02</SectionNumber>
					<SectionTitle>Ratatietojen ylläpito</SectionTitle>
				</div>

				<figure className="mt-10 max-w-3xl">
					<div className="text-primary mb-2 text-5xl leading-none">“</div>
					<blockquote className="text-foreground/95 text-2xl leading-snug font-medium tracking-tight md:text-3xl">
						Tietojen päivitys on jäänyt muutaman ihmisen harteille.
						Hallintapaneeli on kankea, oikeuksien hallinta monimutkaista —
						eivätkä käyttäjät jaksa edes raportoida virheitä.
					</blockquote>
					<figcaption className="text-muted-foreground mt-4 text-sm">
						Nykytila lyhyesti.
					</figcaption>
				</figure>

				<div className="mt-16 grid gap-6 lg:grid-cols-12">
					<div className="lg:col-span-4">
						<ToneLabel tone="solution">Ratkaisu</ToneLabel>
						<h3 className="mt-3 text-xl font-bold tracking-tight">
							Yhteisön voimin, ylläpitäjän valvonnassa.
						</h3>
					</div>
					<ol className="lg:col-span-8 grid gap-4">
						{steps.map((s, i) => (
							<li
								key={s.title}
								className="border-primary/20 bg-background flex gap-4 border p-4"
							>
								<span className="text-primary mt-0.5 font-mono text-sm font-bold">
									{String(i + 1).padStart(2, '0')}
								</span>
								<div>
									<h4 className="text-foreground text-base font-semibold tracking-tight">
										{s.title}
									</h4>
									<p className="text-foreground/80 mt-1 text-sm leading-relaxed">
										{s.body}
									</p>
								</div>
							</li>
						))}
					</ol>
				</div>
			</div>
		</section>
	)
}

/* ────────────────────────────────────────────────────────────────────────── */
/* 03 — Yhteiset prosessit: ambiguous questions → shared framework            */
/* ────────────────────────────────────────────────────────────────────────── */

function SharedProcessesSection() {
	const questions = [
		{
			title: 'Rata vai layout?',
			body: 'Mitkä radat tulkitaan omiksi radoikseen ja mitkä ovat saman radan eri layoutteja?',
		},
		{
			title: 'Iso uudistus',
			body: 'Mitä tehdään, kun rata kokee niin ison uudistuksen, että se ei käytännössä ole enää sama rata?',
		},
		{
			title: 'Rata poistuu käytöstä',
			body: 'Miten toimitaan, kun rata poistuu käytöstä? Säilyykö se sivustolla ja missä muodossa?',
		},
	]

	const frameworks = [
		'Selkeät kriteerit sille, milloin kyseessä on uusi rata ja milloin uusi layout.',
		'Yhtenäinen tapa käsitellä isoja uudistuksia ja säilyttää historia.',
		'Yhdenmukainen prosessi käytöstä poistuneille radoille.',
	]

	return (
		<section
			id="yhteiset-prosessit"
			className="bg-background flex min-h-[85vh] flex-col justify-center"
		>
			<div className="container py-28 md:py-40">
				{/* Title block */}
				<div>
					<SectionNumber>03</SectionNumber>
					<SectionTitle>Yhteiset prosessit</SectionTitle>
					<p className="text-muted-foreground mt-4 max-w-2xl text-base leading-relaxed md:text-lg">
						Vaihtelevat käytännöt jättävät liikaa yksittäisten tekijöiden
						tulkinnan varaan — ja tieto rakentuu epäjohdonmukaisesti.
					</p>
				</div>

				{/* Nykytila — three open questions */}
				<div className="mt-14">
					<ToneLabel tone="problem">Nykytila</ToneLabel>
					<p className="text-foreground/90 mt-3 max-w-3xl text-base leading-relaxed md:text-lg">
						Ratatietojen ylläpidossa on vaihtelevia käytäntöjä. Toistuvat
						kysymykset jäävät jokaisen tekijän itse ratkaistaviksi:
					</p>
					<div className="mt-6 grid gap-4 md:grid-cols-3">
						{questions.map((q) => (
							<div
								key={q.title}
								className="border-destructive/30 bg-destructive/5 border-l-4 h-full p-5"
							>
								<div className="text-destructive font-mono text-xs font-semibold tracking-widest uppercase">
									ongelma
								</div>
								<h3 className="text-foreground mt-2 text-base font-bold tracking-tight">
									{q.title}
								</h3>
								<p className="text-foreground/90 mt-2 text-sm leading-relaxed">
									{q.body}
								</p>
							</div>
						))}
					</div>
				</div>

				{/* Ratkaisu — site as framework */}
				<div className="border-primary/30 bg-primary/5 mt-10 border-l-4 p-6 md:p-8">
					<ToneLabel tone="solution">Ratkaisu</ToneLabel>
					<p className="text-foreground mt-3 text-lg leading-relaxed font-semibold md:text-xl">
						Uusi sivusto luo raamit prosesseille — ratkaisut eivät ole enää
						yksittäisen tekijän harteilla.
					</p>
					<ul className="mt-6 grid gap-3 md:grid-cols-3 md:gap-x-8 md:gap-y-4">
						{frameworks.map((f) => (
							<li
								key={f}
								className="text-foreground/90 flex gap-3 text-base leading-relaxed"
							>
								<span className="text-primary mt-2 inline-block size-1.5 shrink-0 rounded-full bg-current" />
								<span>{f}</span>
							</li>
						))}
					</ul>
					<p className="text-foreground/80 mt-6 text-sm leading-relaxed md:text-base">
						Yhteiset säännöt selkeyttävät kaikkea toimintaa — sekä tekijöille
						että käyttäjille.
					</p>
				</div>
			</div>
		</section>
	)
}

/* ────────────────────────────────────────────────────────────────────────── */
/* 04 — Datan hajaantuminen: 3-step phased solution                           */
/* ────────────────────────────────────────────────────────────────────────── */

function DataFragmentationSection() {
	const phases: { tag: string; title: string; body: string }[] = [
		{
			tag: 'Nyt',
			title: 'Avoin ilmainen API',
			body: 'Tarjoamme ratadatan vapaasti käyttöön avoimen API:n kautta. Kuka tahansa voi hakea ja hyödyntää tietoa omissa sovelluksissaan.',
		},
		{
			tag: 'Kasvuvaihe',
			title: 'Yksi paikka päivittää',
			body: 'Helppo päivitysmalli vetää tekijät päivittämään tiedot täällä — ei jokaisessa sovelluksessa erikseen. Frisbeegolfradat palaa yhdeksi luotettavaksi lähteeksi.',
		},
		{
			tag: 'Tulevaisuudessa',
			title: 'Premier API',
			body: 'Kun käyttö kasvaa, tarjoamme kumppaneille Premier-tason API:n korkeammilla kiintiöillä ja erikoisominaisuuksilla.',
		},
	]

	return (
		<section
			id="datan-hajaantuminen"
			className="bg-background flex min-h-[85vh] flex-col justify-center"
		>
			<div className="container py-28 md:py-40">
				{/* Title block */}
				<div>
					<SectionNumber>04</SectionNumber>
					<SectionTitle>Datan hajaantuminen</SectionTitle>
					<p className="text-muted-foreground mt-4 max-w-2xl text-base leading-relaxed md:text-lg">
						Yhden luotettavan lähteen ajatus on murenemassa — ja samalla
						mahdollisuus.
					</p>
				</div>

				{/* Nykytila — single wide block */}
				<div className="mt-14">
					<ToneLabel tone="problem">Nykytila</ToneLabel>
					<div className="border-destructive/30 bg-destructive/5 mt-4 grid gap-6 border-l-4 p-6 md:p-8 lg:grid-cols-2 lg:gap-12">
						<p className="text-foreground/90 text-base leading-relaxed">
							Useat uudet sovellukset hakevat ratadataamme omiin
							palveluihinsa ja jatkavat sen päivittämistä siellä eteenpäin.
							Tieto eriytyy.
						</p>
						<p className="text-foreground/90 text-base leading-relaxed">
							Lopputulos: tieto pirstoutuu kymmeneen eri sovellukseen, eikä
							yhtä luotettavaa lähdettä enää käytännössä ole. Käyttäjä ei
							tiedä mihin uskoa.
						</p>
					</div>
				</div>

				{/* Ratkaisu — phased steps */}
				<div className="mt-14">
					<ToneLabel tone="solution">Ratkaisu</ToneLabel>
					<p className="text-foreground mt-3 max-w-3xl text-lg leading-relaxed font-semibold md:text-xl">
						Avoin API + helppo päivittäminen vetävät käytön takaisin yhteen
						lähteeseen.
					</p>
					<ol className="mt-8 grid gap-4 md:grid-cols-3 md:gap-6">
						{phases.map((p, i) => (
							<li key={p.title} className="relative">
								<div className="border-primary/30 bg-primary/5 h-full border-l-4 p-5 md:p-6">
									<div className="text-primary font-mono text-xs font-semibold tracking-widest uppercase">
										0{i + 1} · {p.tag}
									</div>
									<h3 className="text-foreground mt-2 text-lg font-bold tracking-tight">
										{p.title}
									</h3>
									<p className="text-foreground/85 mt-2 text-sm leading-relaxed">
										{p.body}
									</p>
								</div>
								{i < phases.length - 1 ? (
									<svg
										xmlns="http://www.w3.org/2000/svg"
										viewBox="0 0 24 24"
										fill="none"
										stroke="currentColor"
										strokeWidth="2"
										strokeLinecap="round"
										strokeLinejoin="round"
										aria-hidden="true"
										className="text-primary/50 absolute top-1/2 -right-3.5 hidden size-5 -translate-y-1/2 md:block"
									>
										<polyline points="9 18 15 12 9 6" />
									</svg>
								) : null}
							</li>
						))}
					</ol>
				</div>
			</div>
		</section>
	)
}

/* ────────────────────────────────────────────────────────────────────────── */
/* Roadmap timeline                                                           */
/* ────────────────────────────────────────────────────────────────────────── */

type Phase = {
	phase: string
	when: string
	title: string
	desc: string
	state: 'current' | 'next' | 'later'
}

const PHASES: Phase[] = [
	{
		phase: 'Vaihe 1',
		when: 'Kesä 2026',
		title: 'Suunnittelu & visio',
		desc: 'Vision tarkentaminen, tarkempi toteutussuunnittelu ja tekninen pohjatyö.',
		state: 'current',
	},
	{
		phase: 'Vaihe 2',
		when: 'Syys–lokakuu 2026',
		title: 'Toteutus & koekäyttö',
		desc: 'Uuden version rakentaminen alkaa, ja ensimmäiset versiot otetaan koekäyttöön testikäyttäjien kanssa.',
		state: 'next',
	},
	{
		phase: 'Vaihe 3',
		when: 'Marras–joulukuu 2026',
		title: 'Julkaisu',
		desc: 'Uusi frisbeegolfradat.fi avataan kaikille käyttäjille.',
		state: 'later',
	},
]

function RoadmapSection() {
	return (
		<section id="roadmap" className="bg-background">
			<div className="container py-28 md:py-40">
				<div className="mx-auto max-w-2xl text-center">
					<SectionNumber>→</SectionNumber>
					<SectionTitle>Roadmap</SectionTitle>
					<p className="text-muted-foreground mt-4 text-base leading-relaxed">
						Vaiheittainen polku suunnittelusta julkiseen palveluun. Tarkat
						aikataulut elävät palautteen mukaan.
					</p>
				</div>

				<ol className="relative mx-auto mt-16 max-w-3xl">
					{/* vertical line */}
					<div className="bg-border absolute top-0 bottom-0 left-4 w-px md:left-1/2 md:-translate-x-1/2" />

					{PHASES.map((phase, i) => {
						const sideRight = i % 2 === 1
						return (
							<li
								key={phase.phase}
								className="relative grid gap-6 pb-12 last:pb-0 md:grid-cols-2"
							>
								{/* dot */}
								<span
									className={`absolute left-4 z-10 size-3.5 -translate-x-1/2 translate-y-2 md:left-1/2 ${
										phase.state === 'current'
											? 'bg-primary ring-primary/30 ring-4'
											: phase.state === 'next'
												? 'bg-primary/70'
												: 'bg-border'
									}`}
								/>

								{/* card — side depends on index on desktop */}
								<div
									className={`pl-12 md:pl-0 ${
										sideRight
											? 'md:col-start-2 md:pl-12'
											: 'md:col-start-1 md:pr-12 md:text-right'
									}`}
								>
									<div className="text-primary text-xs font-mono font-semibold tracking-widest uppercase">
										{phase.phase} · {phase.when}
									</div>
									<h3 className="mt-2 text-xl font-bold tracking-tight md:text-2xl">
										{phase.title}
									</h3>
									<p className="text-muted-foreground mt-2 text-sm leading-relaxed md:text-base">
										{phase.desc}
									</p>
									{phase.state === 'current' ? (
										<span className="bg-primary text-primary-foreground mt-3 inline-block px-2 py-0.5 text-xs font-semibold tracking-wider uppercase">
											Käynnissä
										</span>
									) : null}
								</div>
							</li>
						)
					})}
				</ol>
			</div>
		</section>
	)
}

/* ────────────────────────────────────────────────────────────────────────── */
/* Feedback CTA                                                               */
/* ────────────────────────────────────────────────────────────────────────── */

function FeedbackCta() {
	return (
		<section
			id="palaute"
			className="bg-primary text-primary-foreground flex min-h-[60vh] flex-col justify-center"
		>
			<div className="container py-24 text-center md:py-32">
				<h2 className="text-3xl font-bold tracking-tight md:text-4xl">
					Anna palautetta
				</h2>
				<p className="mx-auto mt-6 max-w-2xl text-lg leading-relaxed opacity-90">
					Nämä ovat alustavia ajatuksia ja suuntaviivoja muutokselle. Moni
					asia voi vielä muuttua — siksi kuulemme mielellämme ajatuksesi,
					toiveesi ja huolenaiheesi.
				</p>
				<div className="mt-10">
					<a
						href="mailto:info@fgr.fi?subject=Palaute%20uudistuvasta%20frisbeegolfradat.fi%3Asta"
						className="bg-background text-primary hover:bg-background/90 inline-flex items-center gap-2 px-6 py-3 text-base font-semibold tracking-tight transition-colors md:text-lg"
					>
						info@fgr.fi
					</a>
				</div>
				<p className="mt-6 text-sm opacity-75">
					Lähetä sähköpostia osoitteeseen yllä — luemme jokaisen viestin.
				</p>
			</div>
		</section>
	)
}

/* ────────────────────────────────────────────────────────────────────────── */
/* Shared bits                                                                */
/* ────────────────────────────────────────────────────────────────────────── */

function SectionNumber({
	children,
	tone = 'light',
}: {
	children: React.ReactNode
	tone?: 'light' | 'dark'
}) {
	return (
		<div
			className={`font-mono text-sm font-semibold tracking-widest ${
				tone === 'dark' ? 'text-primary' : 'text-primary/80'
			}`}
		>
			{children}
		</div>
	)
}

function SectionTitle({
	children,
	tone = 'light',
}: {
	children: React.ReactNode
	tone?: 'light' | 'dark'
}) {
	return (
		<h2
			className={`mt-3 text-3xl font-bold tracking-tight md:text-4xl ${
				tone === 'dark' ? 'text-background' : ''
			}`}
		>
			{children}
		</h2>
	)
}

function ToneLabel({
	children,
	tone,
}: {
	children: React.ReactNode
	tone: 'problem' | 'solution'
}) {
	return (
		<div
			className={`text-xs font-semibold tracking-widest uppercase ${
				tone === 'problem' ? 'text-destructive' : 'text-primary'
			}`}
		>
			{children}
		</div>
	)
}
